import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  User, 
  Settings, 
  Key, 
  LogOut, 
  Trash2,
  ChevronDown,
  Mail,
  Phone,
  Loader2
} from 'lucide-react';

const UserProfileMenu = () => {
  const { user, logout, refreshUser } = useAuth();
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    phone: user?.phone || ''
  });
  
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleUpdateProfile = async () => {
    try {
      setLoading(true);
      await authAPI.updateProfile(profileData);
      toast.success('Perfil actualizado');
      setShowProfileDialog(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    
    try {
      setLoading(true);
      await authAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Contraseña actualizada');
      setShowPasswordDialog(false);
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      setLoading(true);
      await authAPI.deleteAccount();
      toast.success('Cuenta eliminada');
      logout();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar cuenta');
    } finally {
      setLoading(false);
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'owner': return 'bg-purple-100 text-purple-700';
      case 'executive': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-100 transition-colors w-full" data-testid="user-menu-trigger">
            {user?.picture ? (
              <img 
                src={user.picture} 
                alt={user.name}
                className="w-9 h-9 rounded-full object-cover"
              />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-medium">
                {user?.name?.charAt(0)?.toUpperCase()}
              </div>
            )}
            <div className="flex-1 text-left hidden sm:block">
              <p className="text-sm font-medium text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="end" className="w-64 bg-white border-gray-200 shadow-lg">
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-2">
              <p className="text-sm font-medium text-gray-900">{user?.name}</p>
              <p className="text-xs text-gray-500">{user?.email}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full w-fit ${getRoleBadgeColor(user?.role)}`}>
                {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)}
              </span>
            </div>
          </DropdownMenuLabel>
          
          <DropdownMenuSeparator className="bg-gray-100" />
          
          <DropdownMenuItem 
            onClick={() => {
              setProfileData({ name: user?.name || '', phone: user?.phone || '' });
              setShowProfileDialog(true);
            }}
            className="cursor-pointer hover:bg-gray-50"
            data-testid="edit-profile-btn"
          >
            <User className="w-4 h-4 mr-2 text-gray-500" />
            <span>Editar Perfil</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem 
            onClick={() => setShowPasswordDialog(true)}
            className="cursor-pointer hover:bg-gray-50"
            data-testid="change-password-btn"
          >
            <Key className="w-4 h-4 mr-2 text-gray-500" />
            <span>Cambiar Contraseña</span>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator className="bg-gray-100" />
          
          <DropdownMenuItem 
            onClick={() => setShowDeleteDialog(true)}
            className="cursor-pointer hover:bg-red-50 text-red-600"
            data-testid="delete-account-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            <span>Eliminar Cuenta</span>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator className="bg-gray-100" />
          
          <DropdownMenuItem 
            onClick={logout}
            className="cursor-pointer hover:bg-gray-50"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2 text-gray-500" />
            <span>Cerrar Sesión</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Editar Perfil</DialogTitle>
            <DialogDescription className="text-gray-500">
              Actualiza tu información personal
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Nombre</label>
              <Input
                value={profileData.name}
                onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                className="bg-gray-50 border-gray-200 focus:bg-white"
                placeholder="Tu nombre"
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Teléfono</label>
              <Input
                value={profileData.phone}
                onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                className="bg-gray-50 border-gray-200 focus:bg-white"
                placeholder="+1 234 567 8900"
                data-testid="profile-phone-input"
              />
            </div>
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-500">{user?.email}</span>
              <span className="text-xs text-gray-400 ml-auto">No editable</span>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowProfileDialog(false)} className="border-gray-200">
              Cancelar
            </Button>
            <Button 
              onClick={handleUpdateProfile} 
              disabled={loading}
              className="bg-gray-900 hover:bg-gray-800 text-white"
              data-testid="save-profile-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-gray-900">Cambiar Contraseña</DialogTitle>
            <DialogDescription className="text-gray-500">
              Ingresa tu contraseña actual y la nueva contraseña
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Contraseña Actual</label>
              <Input
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                className="bg-gray-50 border-gray-200 focus:bg-white"
                data-testid="current-password-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Nueva Contraseña</label>
              <Input
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                className="bg-gray-50 border-gray-200 focus:bg-white"
                data-testid="new-password-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">Confirmar Nueva Contraseña</label>
              <Input
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                className="bg-gray-50 border-gray-200 focus:bg-white"
                data-testid="confirm-password-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="border-gray-200">
              Cancelar
            </Button>
            <Button 
              onClick={handleChangePassword} 
              disabled={loading}
              className="bg-gray-900 hover:bg-gray-800 text-white"
              data-testid="save-password-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Cambiar Contraseña'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="bg-white border-gray-200">
          <DialogHeader>
            <DialogTitle className="text-red-600">Eliminar Cuenta</DialogTitle>
            <DialogDescription className="text-gray-500">
              Esta acción es irreversible. Se eliminarán todos tus datos permanentemente.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700">
                ¿Estás seguro de que deseas eliminar tu cuenta? Esta acción no se puede deshacer.
              </p>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)} className="border-gray-200">
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteAccount} 
              disabled={loading}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Eliminar Cuenta'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default UserProfileMenu;
